import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/context/AuthContext.jsx'
import { readConsent, writeConsent, clearConsent } from '@/lib/consentStorage.js'
import { setAnalyticsContext, bootstrapAnalytics } from '@/lib/analytics.js'

const ConsentContext = createContext({
  consent: { analytics: 'unset', advertising: 'unset' },
  isUnset: true,
  preferencesOpen: false,
  setPreferencesOpen: () => {},
  grantAnalytics: () => {},
  denyAnalytics: () => {},
  savePreferences: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  resetConsent: () => {},
})

export function ConsentProvider({ children }) {
  const { user } = useAuth()
  const [consent, setConsentState] = useState(() => readConsent())
  const [preferencesOpen, setPreferencesOpen] = useState(false)

  useEffect(() => {
    setAnalyticsContext({ consent, user })
    if (consent.analytics === 'granted') {
      bootstrapAnalytics()
    }
  }, [consent, user])

  const value = useMemo(() => {
    const setConsent = (preferences) => {
      writeConsent({ ...consent, ...preferences })
      setConsentState(readConsent())
    }
    return {
      consent,
      isUnset: consent.analytics === 'unset' || consent.advertising === 'unset',
      preferencesOpen,
      setPreferencesOpen,
      grantAnalytics: () => setConsent({ analytics: 'granted' }),
      denyAnalytics: () => setConsent({ analytics: 'denied' }),
      savePreferences: setConsent,
      acceptAll: () => setConsent({ analytics: 'granted', advertising: 'granted' }),
      rejectAll: () => setConsent({ analytics: 'denied', advertising: 'denied' }),
      resetConsent: () => {
        clearConsent()
        setConsentState(readConsent())
      },
    }
  }, [consent, preferencesOpen])

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

export function useConsent() {
  return useContext(ConsentContext)
}
